import { Button, Radio, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useCallback, useId, useMemo, useState } from 'react';

import {
  ACTIVE_SECTION_KEY,
  EXPANDED_SECTION_KEYS,
  INACTIVE_PAGE_SIZE,
  INACTIVE_SECTION_KEY,
  INITIAL_INACTIVE_LIMIT,
  SHOW_MORE_KEY
} from './constants';
import { useCropSeasonTreeData } from './hooks';
import * as Styled from './styles';
import type { Props } from './types';

const CropSeasonOptions = ({
  options,
  currentValue,
  isLoading,
  isCropSeasonSelectionEnabled,
  onConfirm,
  onOpenChange,
  onCancel,
  onShowMore
}: Props) => {
  const [selectedValue, setSelectedValue] = useState(currentValue);
  const [inactiveLimit, setInactiveLimit] = useState(INITIAL_INACTIVE_LIMIT);
  const [searchValue, setSearchValue] = useState('');
  const radioGroupName = useId();

  const optionByKey = useMemo(
    () => new Map(options.map(option => [option.value, option])),
    [options]
  );
  const currentOption = currentValue
    ? optionByKey.get(currentValue)
    : undefined;
  const hasValidSelection = Boolean(
    selectedValue && optionByKey.has(selectedValue)
  );
  const hasNoOptions =
    isCropSeasonSelectionEnabled && !isLoading && options.length === 0;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (open) {
        setSelectedValue(currentValue);
        setInactiveLimit(INITIAL_INACTIVE_LIMIT);
      }
    },
    [currentValue, onOpenChange]
  );

  const handleCancel = useCallback(
    (closePopover: () => void) => {
      setSelectedValue(currentValue);
      onCancel?.();
      closePopover();
    },
    [currentValue, onCancel]
  );

  const { filteredActiveOptions, filteredInactiveOptions } = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase();
    const matchingOptions = options.filter(option =>
      option.label.toLocaleLowerCase().includes(normalizedSearch)
    );

    return {
      filteredActiveOptions: matchingOptions.filter(option => option.isActive),
      filteredInactiveOptions: matchingOptions.filter(
        option => !option.isActive
      )
    };
  }, [options, searchValue]);

  const treeData = useCropSeasonTreeData({
    activeOptions: filteredActiveOptions,
    inactiveOptions: filteredInactiveOptions,
    searchValue,
    inactiveLimit
  });
  const visibleTreeData =
    hasNoOptions || treeData.length === 0 ? undefined : treeData;

  const handleShowMore = useCallback(() => {
    onShowMore?.();
    setInactiveLimit(limit =>
      Math.min(limit + INACTIVE_PAGE_SIZE, filteredInactiveOptions.length)
    );
  }, [filteredInactiveOptions.length, onShowMore]);

  const renderTitle = useCallback(
    (node: DataNode) => {
      if (node.key === ACTIVE_SECTION_KEY) {
        return <Typography.Text strong>Active Seasons</Typography.Text>;
      }

      if (node.key === INACTIVE_SECTION_KEY) {
        return <Typography.Text strong>Inactive Seasons</Typography.Text>;
      }

      if (node.key === SHOW_MORE_KEY) {
        return (
          <Button type='link' onClick={handleShowMore}>
            Show more
          </Button>
        );
      }

      const option = optionByKey.get(String(node.key));

      if (!option) {
        return null;
      }

      return (
        <Radio
          name={radioGroupName}
          value={option.value}
          checked={selectedValue === option.value}
          onChange={() => setSelectedValue(option.value)}
          onClick={event => event.stopPropagation()}>
          <Typography.Text strong={selectedValue === option.value}>
            {option.label}
          </Typography.Text>
        </Radio>
      );
    },
    [optionByKey, selectedValue, handleShowMore, radioGroupName]
  );

  const renderFooter = useCallback(
    (closePopover: () => void) => (
      <>
        {!filteredActiveOptions.length && !filteredInactiveOptions.length && (
          <Styled.NoResults>No results found</Styled.NoResults>
        )}

        <Styled.ButtonRow>
          <Button onClick={() => handleCancel(closePopover)}>Cancel</Button>
          <Button
            type='primary'
            disabled={!hasValidSelection || isLoading}
            loading={isLoading}
            onClick={() => {
              if (
                selectedValue &&
                optionByKey.has(selectedValue) &&
                !isLoading
              ) {
                onConfirm(selectedValue);
                closePopover();
              }
            }}>
            Apply
          </Button>
        </Styled.ButtonRow>
      </>
    ),
    [
      filteredActiveOptions.length,
      filteredInactiveOptions.length,
      handleCancel,
      selectedValue,
      isLoading,
      onConfirm,
      hasValidSelection,
      optionByKey
    ]
  );

  return (
    <Styled.SeasonPopover
      title='Crop Season'
      placeholder='Select a crop season'
      currentValue={currentOption?.label}
      isLoading={isLoading}
      onOpenChange={handleOpenChange}
      treeData={visibleTreeData}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      filterTreeData={false}
      expandedKeys={EXPANDED_SECTION_KEYS}
      selectable={false}
      checkable={false}
      showLine={false}
      switcherIcon={<span />}
      titleRender={renderTitle}
      showSearch={!hasNoOptions}
      footer={hasNoOptions ? undefined : renderFooter}>
      {hasNoOptions
        ? () => (
            <Styled.EmptyState>
              <Typography.Text strong>Seasons</Typography.Text>
              <Typography.Text>
                No active and inactive crop seasons in this region.
              </Typography.Text>
            </Styled.EmptyState>
          )
        : undefined}
    </Styled.SeasonPopover>
  );
};

export default CropSeasonOptions;
